import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState, type MouseEvent } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { useMouseContent } from 'src/utils/mouse/mouseContent';

import {
	defaultTQC3020_R01SalesDto,
	defaultTQC3020_R01SalesLotDto,
	TQC3020_R01SalesDto,
	TQC3020_R01SalesLotDto,
} from '../Dto_TQC3020_R01';
import { useTQC3020_R01Store } from '../Store_TQC3020_R01';

const SALES_COLUMN_STATE_KEY = 'TQC3020_R01-sales-grid-column-state';
const SALES_LOT_COLUMN_STATE_KEY = 'TQC3020_R01-sales-lot-grid-column-state';

const applyBottomCenterAlign = <T,>(defs: ColDef<T>[], bottomLabelCols: Set<string>): ColDef<T>[] =>
	defs.map((def) => {
		if (!def?.colId || !bottomLabelCols.has(def.colId)) return def;
		return {
			...def,
			cellStyle: (params: { node?: { rowPinned?: string | null } }) =>
				params?.node?.rowPinned === 'bottom' ? { textAlign: 'center' } : null,
		};
	});

interface Props {
	salesGridRef: React.RefObject<AgGridReact<TQC3020_R01SalesDto> | null>;
	salesLotGridRef: React.RefObject<AgGridReact<TQC3020_R01SalesLotDto> | null>;
	onSalesRowClick: (data: TQC3020_R01SalesDto) => void;
	onSalesLotRowClick: (data: TQC3020_R01SalesLotDto) => void;
}

export const TableSalesPane_TQC3020_R01 = ({
	salesGridRef,
	salesLotGridRef,
	onSalesRowClick,
	onSalesLotRowClick,
}: Props) => {
	const {
		TQC3020_R01dataList,
		selectedTQC3020_R01Sales,
		setSelectedTQC3020_R01Sales,
		TQC3020_R01salesLotList,
		selectedTQC3020_R01SalesLot,
		setSelectedTQC3020_R01SalesLot,
		TQC3020_R01salesColumnState,
		setTQC3020_R01SalesColumnState,
		TQC3020_R01salesLotColumnState,
		setTQC3020_R01SalesLotColumnState,
		TQC3020ScrollTop,
		setTQC3020ScrollTop,
	} = useTQC3020_R01Store();

	const [, setHoveredRowIndex] = useState<number | null>(null);
	const [, setHoveredRowIndexLot] = useState<number | null>(null);

	const RESIZER = {
		code: 'TQC3020_R01-main',
		type: 'width' as const,
		storageKey: 'TQC3020_R01-main-split-width',
		min: 20,
		max: 80,
		default: 50,
	} as const;

	const containerRef = useRef<HTMLDivElement>(null);
	const lotTableRef = useRef<HTMLDivElement>(null);
	const isResizing = useRef(false);

	const { splitValue, handleMouseDown } = useMouseContent({
		resizer: RESIZER,
		isResizing,
		containerRef,
		infoRef: lotTableRef,
	});

	const {
		getRowId: getSalesRowId,
		settingRowIndex: salesSettingRowIndex,
		createRowClassRules: createSalesRowClassRules,
		paddedGridData: salesPaddedGridData,
	} = useGridSettingContent({
		gridRef: salesGridRef,
		key: 'valueIndex',
		gridData: TQC3020_R01dataList,
		defaultGridData: {} as TQC3020_R01SalesDto,
		selectedRow: selectedTQC3020_R01Sales,
	});

	const {
		getRowId: getSalesLotRowId,
		settingRowIndex: salesLotSettingRowIndex,
		createRowClassRules: createSalesLotRowClassRules,
		paddedGridData: salesLotPaddedGridData,
	} = useGridSettingContent({
		gridRef: salesLotGridRef,
		key: 'valueIndex',
		gridData: TQC3020_R01salesLotList,
		defaultGridData: {} as TQC3020_R01SalesLotDto,
		selectedRow: undefined,
	});

	const salesBaseColumnDefs: ColDef<TQC3020_R01SalesDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '매출전표번호', colId: 'salesBillNo', field: 'salesBillNo', width: 130,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 80,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '매출일자', colId: 'deliveryDt', field: 'deliveryDt', width: 100,
			valueFormatter: (p) => p.value || '',
		},
		{
			headerName: '거래처', colId: 'customCd', field: 'customCd', width: 100,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '거래처명', colId: 'customNmFull', field: 'customNmFull', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '모델명', colId: 'modelNo', field: 'modelNo', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '매출수량', colId: 'salesQty', field: 'salesQty', width: 80,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
	], []);

	const salesDefaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(salesBaseColumnDefs, [])
	, [salesBaseColumnDefs]);

	const salesColumnDefs = useMemo(() => {
		const merged = TQC3020_R01salesColumnState === null
			? salesBaseColumnDefs
			: GridCustomColumnDefs<TQC3020_R01SalesDto>({
				baseColumnDefs: salesBaseColumnDefs,
				columnState: TQC3020_R01salesColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyBottomCenterAlign(merged, new Set(['salesBillNo']));
	}, [TQC3020_R01salesColumnState, salesSettingRowIndex, salesBaseColumnDefs]);

	const salesLotBaseColumnDefs: ColDef<TQC3020_R01SalesLotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: 'Lot No', colId: 'lotNo', field: 'lotNo', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '수량', colId: 'lotQty', field: 'lotQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
	], []);

	const salesLotDefaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(salesLotBaseColumnDefs, [])
	, [salesLotBaseColumnDefs]);

	const salesLotColumnDefs = useMemo(() =>
		TQC3020_R01salesLotColumnState === null
			? salesLotBaseColumnDefs
			: GridCustomColumnDefs<TQC3020_R01SalesLotDto>({
				baseColumnDefs: salesLotBaseColumnDefs,
				columnState: TQC3020_R01salesLotColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			})
	, [TQC3020_R01salesLotColumnState, salesLotSettingRowIndex, salesLotBaseColumnDefs]);

	const handleSalesRowClicked = useCallback((e: RowClickedEvent<TQC3020_R01SalesDto>) => {
		if (e.node.rowPinned === 'bottom' || !e.data) return;
		setSelectedTQC3020_R01Sales(e.data);
		onSalesRowClick(e.data);
	}, [setSelectedTQC3020_R01Sales, onSalesRowClick]);

	const handleSalesLotRowClicked = useCallback((e: RowClickedEvent<TQC3020_R01SalesLotDto>) => {
		if (e.node.rowPinned === 'bottom' || !e.data) return;
		setSelectedTQC3020_R01SalesLot(e.data);
		onSalesLotRowClick(e.data);
	}, [setSelectedTQC3020_R01SalesLot, onSalesLotRowClick]);

	const salesPinnedBottomRow = useMemo(() => ([{
		salesBillNo: `${TQC3020_R01dataList.length.toLocaleString()}건`,
		salesQty: TQC3020_R01dataList.reduce((sum, item) => sum + (item.salesQty || 0), 0).toLocaleString(),
	}]), [TQC3020_R01dataList]);

	return (
		<div className="flex-1 flex gap-[4px]" ref={containerRef}>
			<div className="h-full" style={{ width: `${splitValue}%` }}>
				<GridContextWrapper<TQC3020_R01SalesDto>
					enableCellCalculation
					gridRef={salesGridRef}
					paddedGridData={salesPaddedGridData}
					initSelected={() => setSelectedTQC3020_R01Sales(undefined)}
					defaultRowData={defaultTQC3020_R01SalesDto}
					baseColumnDefs={TQC3020_R01salesColumnState ? salesColumnDefs : salesBaseColumnDefs}
					defaultColumnState={salesDefaultColumnState}
					setColumnState={setTQC3020_R01SalesColumnState}
					localStorageKey={SALES_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<TQC3020_R01SalesDto>
						ref={salesGridRef}
						rowData={salesPaddedGridData}
						columnDefs={salesColumnDefs}
						defaultColDef={defaultColDef}
						getRowId={getSalesRowId}
						rowClassRules={createSalesRowClassRules<TQC3020_R01SalesDto>({
							idKey: 'valueIndex',
							selectedList: selectedTQC3020_R01Sales?.valueIndex ? [selectedTQC3020_R01Sales.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleSalesRowClicked}
						onCellKeyDown={undefined}
						pinnedBottomRowData={salesPinnedBottomRow}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={SALES_COLUMN_STATE_KEY}
						setColumnState={setTQC3020_R01SalesColumnState}
						scrollTop={TQC3020ScrollTop.sales}
						setScrollTop={(top) => setTQC3020ScrollTop('sales', top)}
					/>
				</GridContextWrapper>
			</div>

			<div
				className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
				onMouseDown={(e: MouseEvent<HTMLDivElement>) => handleMouseDown('LR', e.nativeEvent)}
				data-comment="리사이저1"
			/>

			<div className="h-full" style={{ width: `${100 - splitValue}%` }} ref={lotTableRef}>
				<GridContextWrapper<TQC3020_R01SalesLotDto>
					enableCellCalculation
					gridRef={salesLotGridRef}
					paddedGridData={salesLotPaddedGridData}
					initSelected={() => setSelectedTQC3020_R01SalesLot(undefined)}
					defaultRowData={defaultTQC3020_R01SalesLotDto}
					baseColumnDefs={TQC3020_R01salesLotColumnState ? salesLotColumnDefs : salesLotBaseColumnDefs}
					defaultColumnState={salesLotDefaultColumnState}
					setColumnState={setTQC3020_R01SalesLotColumnState}
					localStorageKey={SALES_LOT_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<TQC3020_R01SalesLotDto>
						ref={salesLotGridRef}
						rowData={salesLotPaddedGridData}
						columnDefs={salesLotColumnDefs}
						defaultColDef={defaultColDef}
						getRowId={getSalesLotRowId}
						rowClassRules={createSalesLotRowClassRules<TQC3020_R01SalesLotDto>({
							idKey: 'valueIndex',
							selectedList: selectedTQC3020_R01SalesLot?.valueIndex ? [selectedTQC3020_R01SalesLot.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleSalesLotRowClicked}
						onCellKeyDown={undefined}
						setHoveredRowIndex={setHoveredRowIndexLot}
						columnStateKey={SALES_LOT_COLUMN_STATE_KEY}
						setColumnState={setTQC3020_R01SalesLotColumnState}
						scrollTop={TQC3020ScrollTop.salesLot}
						setScrollTop={(top) => setTQC3020ScrollTop('salesLot', top)}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
